import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * LeftSidebarDesktop Component
 * 
 * Desktop version of the left sidebar.
 * Exact copy of original LeftSidebar with touch feedback added and mobile CSS removed.
 */
const LeftSidebarDesktop = ({
  collapsed = false,
  onToggleCollapse,
  onNewChat,
  sessions = [],
  currentSessionId,
  onSelectSession,
  userName = 'User',
  onProfileClick,
  theme = 'dark',
  onDeleteSession
}) => {
  const navigate = useNavigate();

  const chats = sessions;

  return (
    <aside
      id="sidebar"
      className={`sidebar fixed left-0 top-0 h-screen flex flex-col z-[100] ${collapsed ? 'collapsed' : ''
        }`}
      style={{
        width: collapsed ? '80px' : '260px',
        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(248, 249, 250, 0.7)',
        backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        borderRight: theme === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(148, 163, 184, 0.6)',
        padding: collapsed ? '12px 6px' : '12px',
        transition: 'width 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        boxShadow: theme === 'light'
          ? '0 8px 32px 0 rgba(15, 23, 42, 0.1), 0 2px 8px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8), inset -1px 0 0 rgba(148, 163, 184, 0.2)'
          : 'none',
      }}
      data-theme={theme}
    >
      {/* Sidebar Header */}
      <div className="sidebar-header mb-3">
        <div
          className="logo-title text-[20px] font-bold whitespace-nowrap cursor-pointer transition-opacity duration-200"
          style={{
            color: theme === 'dark' ? '#ffffff' : '#0f172a',
            display: collapsed ? 'none' : 'block',
          }}
          onClick={() => navigate('/')}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <span className="lawnance-brand">LAWNANCE</span>
        </div>
      </div>

      {/* Hamburger Toggle Button */}
      <button
        className="sidebar-hamburger-toggle w-10 h-10 rounded-full border-none cursor-pointer flex items-center justify-center mb-3 active:scale-[0.98]"
        id="sidebarToggle"
        aria-label="Toggle sidebar"
        onClick={onToggleCollapse}
        style={{
          background: theme === 'dark' ? 'rgba(30, 30, 30, 0.9)' : 'rgba(240, 240, 240, 0.9)',
          color: theme === 'dark' ? '#ffffff' : '#0f172a',
          boxShadow: theme === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(15, 23, 42, 0.1)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(40, 40, 40, 0.95)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
          } else {
            e.currentTarget.style.background = 'rgba(250, 250, 250, 0.95)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(15, 23, 42, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(30, 30, 30, 0.9)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.4)';
          } else {
            e.currentTarget.style.background = 'rgba(240, 240, 240, 0.9)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.1)';
          }
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {/* New Chat Button */}
      <button
        className="new-chat-btn w-full border-none rounded-full flex items-center justify-center cursor-pointer mb-3 active:scale-[0.98]"
        onClick={onNewChat}
        style={{
          background: theme === 'dark' ? 'rgba(20, 20, 20, 0.8)' : 'rgba(240, 240, 240, 0.8)',
          padding: collapsed ? '12px' : '14px 12px',
          gap: '6px',
          color: theme === 'dark' ? '#ffffff' : '#0f172a',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: theme === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(15, 23, 42, 0.1)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(30, 30, 30, 0.9)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4)';
          } else {
            e.currentTarget.style.background = 'rgba(250, 250, 250, 0.9)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.15)';
          }
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(20, 20, 20, 0.8)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
          } else {
            e.currentTarget.style.background = 'rgba(240, 240, 240, 0.8)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.1)';
          }
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <span className="new-chat-icon flex items-center justify-center w-[18px] h-[18px] flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 2V16M2 9H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <span
          className="whitespace-nowrap"
          style={{
            display: collapsed ? 'none' : 'inline',
          }}
        >
          New Chat
        </span>
      </button>

      {/* Recent Section Title */}
      <div
        className="sidebar-section-title text-[11px] uppercase mb-3 whitespace-nowrap"
        style={{
          letterSpacing: '0.15em',
          color: theme === 'dark' ? '#9ca3af' : '#475569',
          display: collapsed ? 'none' : 'block',
        }}
      >
        RECENT
      </div>

      {/* Recent List */}
      <ul
        className="recent-list flex-1 overflow-y-auto mb-3 list-none"
        id="recentList"
      >
        {chats.map((chat) => {
          const isActive = chat.id === currentSessionId;
          const activeBg = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.6)';

          return (
            <li
              key={chat.id}
              className="cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis active:scale-[0.98]"
              onClick={() => onSelectSession?.(chat.id)}
              style={{
                padding: '10px 6px',
                borderRadius: '8px',
                fontSize: '13px',
                color: isActive ? (theme === 'dark' ? '#ffffff' : '#0f172a') : (theme === 'dark' ? '#888888' : '#1e293b'),
                background: isActive ? activeBg : 'transparent',
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  if (theme === 'dark') {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.color = '#ffffff';
                  } else {
                    e.currentTarget.style.background = 'rgba(249, 250, 251, 0.8)';
                    e.currentTarget.style.color = '#0f172a';
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = theme === 'dark' ? '#888888' : '#1e293b';
                }
              }}
            >
              <span style={{ display: collapsed ? 'none' : 'inline', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {chat.title || 'New Chat'}
              </span>
              {!collapsed && (
                <button
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSession?.(chat.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px',
                    marginLeft: '4px',
                    cursor: 'pointer',
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme === 'dark' ? '#ff6b6b' : '#ef4444';
                    e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Sidebar Footer */}
      <div
        className="sidebar-footer flex items-center cursor-pointer rounded-lg active:scale-[0.98]"
        style={{
          gap: '6px',
          padding: collapsed ? '12px 6px' : '6px',
          paddingTop: collapsed ? undefined : '12px',
          borderTop: theme === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(203, 213, 225, 0.8)',
          marginTop: '12px',
          flexDirection: collapsed ? 'column' : 'row',
          justifyContent: collapsed ? 'center' : 'flex-start',
          transition: 'all 0.2s ease',
        }}
        onClick={onProfileClick}
        onMouseEnter={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
          } else {
            e.currentTarget.style.background = 'rgba(249, 250, 251, 0.8)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        {/* User Avatar */}
        <div
          className="user-avatar w-8 h-8 rounded-full flex-shrink-0 relative"
          style={{
            background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              display: 'block',
              margin: '0 auto',
            }}
          >
            <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 14C3 11 5.5 9 8 9C10.5 9 13 11 13 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>

        {/* User Name */}
        <span
          className="user-name text-[13px] whitespace-nowrap"
          style={{
            color: theme === 'dark' ? '#ffffff' : '#0f172a',
            display: collapsed ? 'none' : 'inline',
          }}
        >
          {userName}
        </span>
      </div>
    </aside>
  );
};

export default LeftSidebarDesktop;

